// §34.4 / §9.1 — Quick Notes' registration stub.
//
// **Manifest and thunk only** (D-511), like Archive: a utility registered as a constant puts its
// whole view in the boot path whether or not anyone opens it, and the chunk this thunk produces is
// named `lazy.Notes-<hash>.js` so the same exact-prefix rule keeps it out of the precache.
//
// It registers **no file types**. A note is a `.md`, and `.md` belongs to Notepad — §34.4's whole
// point is that "Notepad opens any note", so claiming the extension here would take it away from the
// editor the notes were designed to be readable in.
import type { InProcessApp, NotesShellAccess } from '@hangar/shared';
import { createLazyApp } from '@/gamekit/lazy';
import { COPY } from '@/copy';
import { NOTES_WINDOW } from '@/config';

export function createNotesApp(access: NotesShellAccess): InProcessApp {
  // §34.4 — the one grant, taken **at registration** rather than on first mount (D-545). The card
  // has to summon from any app, which means the chord must work before this app has ever been
  // opened; a capability claimed inside `mount` would arm the key only after someone launched the
  // window it exists to avoid needing.
  access.registerCaptureChord();

  return createLazyApp({
    app: {
      id: 'notes',
      name: COPY.notes.title,
      // §34.1 (D-505) — the wave's launcher home; the glyph is the existing `sticky-note`, reused
      // rather than transcribed (D-506), so §22's one-icon-set rule is untouched.
      category: 'Utilities',
      icon: { glyph: 'sticky-note', hue: 48 },
      keywords: ['note', 'notes', 'quick', 'capture', 'jot', 'scratch'],
      // Three window shapes live behind this one app: the library, the capture card, and a pinned
      // note (§34.4). All three are windows, so `multiWindow` is what makes them possible.
      window: { ...NOTES_WINDOW, multiWindow: true },
    },
    load: () => import('./Notes'),
    props: { access },
  });
}
